import { TypedDocumentNode, gql } from '@apollo/client';
import { MessageDescriptor, defineMessages } from 'react-intl';

import {
  UserAccountEntryState,
  WithdrawalStatus,
} from '__generated__/globalTypes';
import { isType } from 'lib/gql';

import { DumbAccountEntryState } from './DumbAccountEntryState';
import { AccountEntryState_userAccountEntry } from './__generated__/index.graphql';

type Props = {
  userAccountEntry: AccountEntryState_userAccountEntry;
};

const withdrawalStatusMessages = defineMessages({
  [WithdrawalStatus.CREATED]: {
    id: 'AccountEntryStatus.pendingEmailConfirmation',
    defaultMessage: 'Pending email confirmation',
  },
  [WithdrawalStatus.CONFIRMED]: {
    id: 'AccountEntryStatus.confirmed',
    defaultMessage: 'Pending',
  },
  [WithdrawalStatus.SETTLEMENT_PUBLISHED]: {
    id: 'AccountEntryStatus.settlementPublished',
    defaultMessage: 'Pending',
  },
  [WithdrawalStatus.CANCELLED]: {
    id: 'AccountEntryStatus.cancelled',
    defaultMessage: 'Canceled',
  },
  [WithdrawalStatus.SETTLEMENT_FAILED]: {
    id: 'AccountEntryStatus.error',
    defaultMessage: 'Error',
  },
  [WithdrawalStatus.SETTLED]: {
    id: 'AccountEntryStatus.settled',
    defaultMessage: 'Settled',
  },
});

const withdrawalStateMessages = defineMessages({
  [UserAccountEntryState.PENDING]: {
    id: 'AccountEntryState.pending',
    defaultMessage: 'Pending',
  },
  [UserAccountEntryState.CONFIRMED]: {
    id: 'AccountEntryState.confirmed',
    defaultMessage: 'Confirmed',
  },
  [UserAccountEntryState.CANCELLED]: {
    id: 'AccountEntryState.cancelled',
    defaultMessage: 'Canceled',
  },
});

export const AccountEntryState = ({ userAccountEntry }: Props) => {
  const { aasmState, tokenOperation } = userAccountEntry;

  if (
    ![
      UserAccountEntryState.PENDING,
      UserAccountEntryState.CANCELLED,
    ].includesValue(userAccountEntry.aasmState)
  )
    return null;

  const isWithDrawal =
    isType(tokenOperation, 'Withdrawal') ||
    isType(tokenOperation, 'FastWithdrawal');

  let label: MessageDescriptor | null = null;
  if (aasmState === UserAccountEntryState.PENDING && isWithDrawal) {
    label = withdrawalStatusMessages[tokenOperation.status];
  } else {
    label = withdrawalStateMessages[aasmState];
  }

  if (!label) return null;

  return <DumbAccountEntryState state={aasmState} label={label} />;
};

AccountEntryState.fragments = {
  userAccountEntry: gql`
    fragment AccountEntryState_userAccountEntry on UserAccountEntry {
      id
      aasmState
      tokenOperation {
        ... on Node {
          id
        }
        ... on FastWithdrawal {
          id
          status
        }
        ... on Withdrawal {
          id
          status
        }
      }
    }
  ` as TypedDocumentNode<AccountEntryState_userAccountEntry>,
};
